import type HlsType from 'hls.js'
import type { PlaybackMode, QualityLevel, VideoSourceConfig } from '../types'

export type QualityChangeHandler = (levels: QualityLevel[], activeId: string, label: string) => void

export class QualityManager {
  private video: HTMLVideoElement
  private hls: HlsType | null = null
  private mode: PlaybackMode = 'mp4'
  private mp4Levels: QualityLevel[] = []
  private activeId = 'auto'
  private onChange: QualityChangeHandler
  private contentConfig: VideoSourceConfig | null = null
  private onFatalError: ((message: string) => void) | null = null
  private onLiveChange: ((live: boolean) => void) | null = null

  constructor(video: HTMLVideoElement, onChange: QualityChangeHandler) {
    this.video = video
    this.onChange = onChange
  }

  setErrorHandler(fn: (message: string) => void) {
    this.onFatalError = fn
  }

  setLiveHandler(fn: (live: boolean) => void) {
    this.onLiveChange = fn
  }

  getHls() {
    return this.hls
  }

  isLive(): boolean {
    return !!this.hls?.levels?.length && (this.hls as HlsType & { liveSyncPosition?: number }).liveSyncPosition != null
      ? true
      : !!(this.hls && !Number.isFinite(this.video.duration))
  }

  jumpToLive() {
    if (!this.hls) return
    const live = this.hls.liveSyncPosition
    if (typeof live === 'number') this.video.currentTime = live
  }

  getHlsAudioTracks(): { id: number; name: string }[] {
    if (!this.hls?.audioTracks) return []
    return this.hls.audioTracks.map((t, id) => ({ id, name: t.name || `Audio ${id + 1}` }))
  }

  setHlsAudioTrack(index: number) {
    if (!this.hls) return
    this.hls.audioTrack = index
  }

  destroy() {
    this.destroyHls()
  }

  private destroyHls() {
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
  }

  getActiveId() {
    return this.activeId
  }

  getLabel(): string {
    if (this.mode === 'hls' && this.activeId === 'auto' && this.hls) {
      const lvl = this.hls.levels[this.hls.currentLevel]
      if (lvl?.height) return `Auto ${lvl.height}p`
      return 'Auto'
    }
    const levels = this.getLevels()
    return levels.find((l) => l.id === this.activeId)?.label ?? this.activeId
  }

  getLevels(): QualityLevel[] {
    if (this.mode === 'mp4') return this.mp4Levels
    if (!this.hls) return [{ id: 'auto', label: 'Auto', hlsLevel: -1 }]
    const auto: QualityLevel = { id: 'auto', label: 'Auto', hlsLevel: -1 }
    const mapped = this.hls.levels.map((lvl, index) => ({
      id: `hls-${index}`,
      label: lvl.height ? `${lvl.height}p` : `Level ${index}`,
      height: lvl.height,
      bitrate: lvl.bitrate,
      hlsLevel: index,
    }))
    return [auto, ...mapped]
  }

  private emit() {
    this.onChange(this.getLevels(), this.activeId, this.getLabel())
  }

  async loadContent(config: VideoSourceConfig, startTime = 0, autoplay = false): Promise<void> {
    this.contentConfig = config
    this.mode = config.mode
    this.destroyHls()
    this.video.removeAttribute('src')
    this.video.load()

    if (config.mode === 'mp4') {
      this.mp4Levels = (config.mp4Qualities ?? []).filter((q) => q.src)
      const preferred =
        this.mp4Levels.find((q) => q.id === this.activeId) ??
        this.mp4Levels[this.mp4Levels.length - 1] ??
        this.mp4Levels[0]
      this.activeId = preferred?.id ?? '360p'
      if (preferred?.src) {
        await this.attachSrc(preferred.src, startTime, autoplay)
      }
      this.emit()
      return
    }

    const url = config.hlsUrl
    if (!url) return

    const Hls = (await import('hls.js')).default
    this.activeId = 'auto'

    if (Hls.isSupported()) {
      this.hls = new Hls({
        enableWorker: true,
        startLevel: -1,
      })
      this.hls.attachMedia(this.video)
      this.hls.on(Hls.Events.MANIFEST_PARSED, async () => {
        this.emit()
        const live = !!(this.hls && (this.hls.levels[0] as { details?: { live?: boolean } })?.details?.live)
          || this.hls?.media?.duration === Infinity
          || !Number.isFinite(this.video.duration)
        // hls.js exposes live via latestLevelDetails
        const detailsLive = this.hls?.levels?.some((l) => (l as { details?: { live?: boolean } }).details?.live)
        this.onLiveChange?.(!!detailsLive || live)
        if (startTime > 0) this.video.currentTime = startTime
        if (autoplay) {
          try {
            await this.video.play()
          } catch {
            /* autoplay blocked */
          }
        }
      })
      this.hls.on(Hls.Events.LEVEL_SWITCHED, () => this.emit())
      this.hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          this.onFatalError?.(data.type || 'hls_error')
        }
      })
      this.hls.loadSource(url)
    } else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      await this.attachSrc(url, startTime, autoplay)
      this.emit()
    }
  }

  async loadPlainSrc(src: string, startTime = 0, autoplay = true): Promise<void> {
    this.destroyHls()
    await this.attachSrc(src, startTime, autoplay)
  }

  private attachSrc(src: string, startTime: number, autoplay: boolean): Promise<void> {
    return new Promise((resolve) => {
      const onMeta = async () => {
        this.video.removeEventListener('loadedmetadata', onMeta)
        if (startTime > 0 && Number.isFinite(startTime)) {
          this.video.currentTime = startTime
        }
        if (autoplay) {
          try {
            await this.video.play()
          } catch {
            /* ignore */
          }
        }
        resolve()
      }
      this.video.addEventListener('loadedmetadata', onMeta)
      this.video.src = src
      this.video.load()
    })
  }

  async setQuality(id: string): Promise<void> {
    if (this.mode === 'mp4') {
      const level = this.mp4Levels.find((l) => l.id === id)
      if (!level?.src) return
      const time = this.video.currentTime
      const wasPaused = this.video.paused
      this.activeId = id
      await this.attachSrc(level.src, time, !wasPaused)
      this.emit()
      return
    }

    if (!this.hls) return
    if (id === 'auto') {
      this.hls.currentLevel = -1
      this.activeId = 'auto'
    } else {
      const level = this.getLevels().find((l) => l.id === id)
      if (level?.hlsLevel == null || level.hlsLevel < 0) return
      this.hls.currentLevel = level.hlsLevel
      this.activeId = id
    }
    this.emit()
  }

  async resumeContent(startTime: number, autoplay: boolean): Promise<void> {
    if (!this.contentConfig) return
    await this.loadContent(this.contentConfig, startTime, autoplay)
  }
}
